import dayjs from 'dayjs';
import axios from 'axios';
import router from './router';
import store from './store';
import ImageResize from 'image-resize';
import html2canvas from 'html2canvas';
import 'dayjs/locale/ko';
import { normalizeAddr } from '@/utils/format';
// import _ from 'lodash';
var weekday = require('dayjs/plugin/weekday');
dayjs.locale('ko');
dayjs.extend(weekday);

axios.defaults.headers.common.Accept = 'application/json';

// 파일 업로드(apiIMG/apiFILE/apiPOST_ADD)·다운로드(apiDOWN)는 대용량 파일·느린
// 회선에서 정상적으로도 오래 걸리므로, apiGET/apiPOST 의 120s 안전망보다 길게(5분)
// 잡아 오탐(false timeout)을 막되, 무한 스피너는 막도록 상한은 둔다.
const FILE_TRANSFER_TIMEOUT_MS = 300000;

// 401(토큰 만료)로 로그인 화면으로 강제 이동시킬 때 "인증 때문에 옮겼다" 는 표시를 남긴다.
// 저장 중 화면 이탈을 막는 화면(BoardWrite 등)이 사용자의 자발적 이동(로그아웃 포함)과
// 인증 만료 리다이렉트를 구분해야 하기 때문이다. 구분이 없으면 401 리다이렉트가 확인창에
// 가로막히거나(사용자가 로그인 화면으로 못 감), 반대로 로그아웃이 확인 없이 통과한다.
let _authRedirectAt = 0;
function gotoLoginByAuth() {
	_authRedirectAt = Date.now();
	router.push({ name: 'LoginView' });
}
// 방금(기본 5초 내) 401 리다이렉트가 있었는지. 라우터 가드에서만 쓴다.
function isAuthRedirect(withinMs = 5000) {
	return Date.now() - _authRedirectAt < withinMs;
}

// 저장·업로드처럼 중간에 끊기면 결과를 확인할 수 없는 작업이 도는지 알린다.
// 헤더의 로그아웃은 서버 로그아웃 → 토큰 삭제 → 화면 이동 순서라, 화면 쪽 이탈 확인창이
// 뜰 때는 이미 토큰이 사라진 상태다. 그래서 로그아웃 버튼이 먼저 물어볼 수 있도록
// "지금 바쁘다" 를 전역으로 노출한다.
let _busyCount = 0;
function setBusy(on) {
	_busyCount = Math.max(0, _busyCount + (on ? 1 : -1));
}
function isBusy() {
	return _busyCount > 0;
}
// API 계층이 실패 사유를 이미 alert 으로 보여줬는지(또는 로그인 화면으로 보냈는지).
// 화면이 뒤이어 일반 문구("저장 실패. 다시 시도해 주세요.")를 띄우면 사용자가 마지막에 보는
// 문장이 원인 설명을 덮어버린다 — 길이 초과처럼 사용자가 직접 고칠 수 있는 안내일 때 특히 나쁘다.
let _apiAlertAt = 0;
function markApiAlert() {
	_apiAlertAt = Date.now();
}
function didApiAlert(withinMs = 5000) {
	return Date.now() - _apiAlertAt < withinMs;
}

// 사용자가 이미 "나가겠다" 고 확인했음(로그아웃 등) — 화면 가드가 같은 질문을 반복하지 않게 한다.
let _leaveConfirmedAt = 0;
function markLeaveConfirmed() {
	_leaveConfirmedAt = Date.now();
}
function isLeaveConfirmed(withinMs = 5000) {
	return Date.now() - _leaveConfirmedAt < withinMs;
}

// F-2.1: API 응답에 포함된 모든 jibun_addr / road_addr 필드를
// $normalizeAddr 와 동일한 규칙(서울특별시·서울시→서울, 번지 제거,
// 공백 정리)으로 일괄 정규화한다. 70+ 의 v-for 템플릿이 raw 로
// 렌더하더라도 ingestion boundary 에서 이미 축약된 문자열을 받기 때문에
// "서울특별시"/"서울시"/번지 가 노출되지 않는다.
//
// 신규 저장 경로는 backend shortAdr() 로 정규화되지만, legacy row 와
// 일부 API (V-World 직접 응답 등) 는 raw 가 그대로 흐르므로 frontend
// 에서 한 번 더 안전망을 친다. 멱등이라 이미 정규화된 값은 변하지 않는다.
// snake_case (DB row): jibun_addr / road_addr / show_addr
// camelCase (Naver Geocode + scraping): jibunAddress / roadAddress / exposureAddress
const _ADDR_KEYS = new Set([
	'jibun_addr',
	'road_addr',
	'show_addr',
	'jibunAddress',
	'roadAddress',
	'exposureAddress',
]);
function _normalizeApiAddrs(node) {
	if (!node || typeof node !== 'object') return node;
	if (Array.isArray(node)) {
		for (let i = 0; i < node.length; i++) _normalizeApiAddrs(node[i]);
		return node;
	}
	const keys = Object.keys(node);
	for (let i = 0; i < keys.length; i++) {
		const k = keys[i];
		const v = node[k];
		if (typeof v === 'string') {
			if (_ADDR_KEYS.has(k)) node[k] = normalizeAddr(v);
		} else if (v && typeof v === 'object') {
			_normalizeApiAddrs(v);
		}
	}
	return node;
}

const ITEM_STATE = { sell: '매각', ready: '준비', hold: '보류', done: '매물' };
const ITEM_STATE2 = { sell: '임대완료', ready: '준비', hold: '보류', done: '매물' };

const PY_M2_EX = 3.3058;
const M2_PY_EX = 0.3025;

const COM_INFO = {
	MEM_RANK: 'mem_rank',
	TEAM: 'team',
	TEAM_CATE: 'team_cate',
	EXPERT_PART: 'expert_part',
	mem_rank: '직급',
	team: '팀',
	team_cate: '하위 팀',
	expert_part: '전문분야',
};

const BUILDING_INFO = {
	BD_CATE: 'bd_cate',
	USE_AREA: 'use_area',
	STRUCTURE: 'structure',
	GMOK: 'gmok',
	PARK_TYPE: 'park_type',
	HEAT_TYPE: 'heat_type',
	WORK_TYPE: 'work_type',
	bd_cate: '매물종류',
	use_area: '용도지역',
	structure: '건물구조',
	gmok: '지목',
	park_type: '주차방식',
	heat_type: '냉/난방',
	work_type: '작업구분',
};

const MONEY_FORMAT_TYPE = {
	LITTLE: 'LITTLE',
	MUCH: 'MUCH',
	LOAN: 'LOAN',
	RANGE: 'RANGE',
	TAX: 'TAX',
	MOBILE: 'MOBILE',
	MOBILE2: 'MOBILE2',
	COMPACT: 'COMPACT',
};

const CLIENT_PHONE_REASON = ['매각 유무 확인', '네고 시도', 'AB해제 시도', '직접입력'];
// DB(2026-10-13): 매수고객 연락처 조회사유. 대표 요청 문구 그대로이고 직접입력은 소유주 쪽과 같이 둔다.
const CLIENT_CONTACT_REASON = ['매수 보류고객 통화', '매수고객 확인', '연락처 확인', '직접입력'];

const CLIENT_LOG_TYPE = {
	mgr_level: '관리등급변경',
	chg_mgr: '관리자변경',
	chg_mobile: '휴대폰변경',
	client_type: '고객분류변경',
	etc: '기타',
};

const LOG_CHG_INFO = {
	sell_status: '진행상태',
	chk_status: '상태',
	open_flag: '공개여부',
	building_price: '건물가',
	building_price_py_price: '건물평당가격',
	land_size_p: '대지면적',
	land_size_m2: '대지면적',
	use_area_uid: '용도지역',
	substation: '주변역',
	substation_distance: '주변역거리',
	public_land_price: '공시지가',
	public_land_price_py_price: '평당공시지가',
	roadwide: '도로사항',
	total_size_p: '연면적',
	total_size_m2: '연면적',
	build_size_p: '건물면적',
	build_size_m2: '건물면적',
	floor_cnt_B: '지하층수',
	floor_cnt_F: '지상층수',
	build_date: '준공일자',
	remodel_date: '리모델링',
	bl_ratio: '건폐율',
	fa_ratio: '용적률',
	park_type_uid: '주차방식',
	park_cnt_law: '법정주차',
	park_cnt_real: '실제주차',
	heat_type_uid: '냉/난방방식',
	ev_cnt: '승강기수',
	delete: '물건삭제',
	un_del: '물건삭제',
	ins_price: '입금가',
	sell_price: '매매금액',
	building_mem_uid: '담당자',
	building_name: '물건명',
	building_addr: '물건주소',
	ex_sum_rent_deposit: '보증금',
	ex_sum_rent_money: '월임대료',
	ex_sum_mgr_fee: '관리비',
	ex_sum_mgr_fee_out: '관리비지출',
	owner_info: '소유자정보',
	toss_building_mem_uid: '위탁관리지정',
	memo: '특징',
	cont_pay_date: '계약일',
	balance_date: '잔금일',
	mid_pay_date: '중도금',
	working_status: '작업상태',

	deposit: '임대 보증금',
	net_area: '전용면적',
	monthly_rent: '임대 월임대료',
	main_fee: '임대 관리비',
	floor_info: '임대 층정보',
	free_parking: '무료주차대수',
	fee_paring: '유로주차대수',
	in_status: '임대 입주현황',
	bathroom_type: '화장실유형',
	rent_area: '임대면적',
	interior_type: '인테리어',
	rent_free: '렌트프리',
};

const INTERIOR_TYPE = {
	need: '필요',
	good: '양호',
	demo: '철거완료',
};

const RENT_FREE = {
	none: '협의',
	confer: '0개월',
};

const CLIENT_TYPE = {
	MANAGE: 'manage',
	NONE: 'none',
	PROPERTY: 'property',
	BLACK: 'black',
	CONTRACT: 'contract',
	manage: '관리',
	none: '미관리',
	property: '부동산',
	black: '블랙',
	contract: '계약',
	lessee: '임차인',
	landlord: '임대인',
	LESSEE: 'lessee',
	LANDLORD: 'landlord',
};

// CF/CG: 직급별 매매 고객 열람범위 (ind_cfg[com_info/mem_rank].cfg_val4).
// 시스템관리 > 직원코드관리 > 직급 탭에서 관리자가 직급마다 고른다. 백엔드 util/clientScope.util.js 가
// 같은 값을 읽는다(CY 2026-10-07). 값을 바꾸면 양쪽을 같이 고칠 것.
//   OWN(N/빈값) 기본: 일반 직원은 본인 담당만, 팀장은 본인 + 팀원 담당. 본부(mem_uid=1) 고객은 넣지 않는다.
//   NONE_OPEN(Y) 기본 + 미관리(client_type='none') 전체 · ALL(A) 전체 고객. master 는 설정과 무관하게 전체.
const CLIENT_VIEW_SCOPE = {
	OWN: 'N',
	NONE_OPEN: 'Y',
	ALL: 'A',
};

const CLIENT_VIEW_SCOPE_LIST = [
	{ value: CLIENT_VIEW_SCOPE.OWN, label: '본인 담당(팀장은 팀)' },
	{ value: CLIENT_VIEW_SCOPE.NONE_OPEN, label: '위 + 미관리 고객' },
	{ value: CLIENT_VIEW_SCOPE.ALL, label: '전체 고객' },
];

// CH: 직급별 업무현황 열람범위 (ind_cfg[com_info/mem_rank].cfg_val3).
// 업무현황판(직원별 작업내역·실적)에서 그 직급이 누구의 데이터까지 보는지. 백엔드
// scope.util.js 의 SCOPE_LEVELS / rankScopeFromRow() 와 값·fallback 이 같아야 한다.
// 값이 비었거나 모르는 값이면 양쪽 모두 기존 직급명 규칙으로 떨어진다(대표·이사=전체, 팀장=팀).
// mem_type='master' 계정은 설정과 무관하게 항상 전체 — 관리자가 자기 화면을 잠그지 않도록.
const WORKBOARD_SCOPE = {
	ALL: 'all',
	TEAM: 'team',
	SELF: 'self',
};

const WORKBOARD_SCOPE_LIST = [
	{ value: WORKBOARD_SCOPE.ALL, label: '전체' },
	{ value: WORKBOARD_SCOPE.TEAM, label: '본인 팀' },
	{ value: WORKBOARD_SCOPE.SELF, label: '본인' },
];

// 설정이 비어 있는 직급을 화면에 표시할 때 쓰는 기본값 — 백엔드 fallback 과 같은 규칙.
const WORKBOARD_SCOPE_RANK_DEFAULT = { 대표: 'all', 이사: 'all', 팀장: 'team' };

const HIRE_STATUS = {
	Y: '재직',
	N: '퇴사',
};

const MEM_TYPE = {
	master: '관리자',
	admin: '직원',
};

const MONEY1 = {
	masked: false,
	prefix: '',
	suffix: '',
	thousands: ',',
	decimal: '.',
	precision: 0,
	disableNegative: false,
	disabled: false,
	min: null,
	max: null,
	allowBlank: false,
	minimumNumberOfCharacters: 0,
	shouldRound: true,
	focusOnRight: false,
};

const MONEY2 = {
	masked: false,
	prefix: '',
	suffix: '',
	thousands: ',',
	decimal: '.',
	precision: 2,
	disableNegative: false,
	disabled: false,
	min: null,
	max: null,
	allowBlank: false,
	minimumNumberOfCharacters: 0,
	shouldRound: true,
	focusOnRight: false,
};

const IMG_NAME = {
	0: '건물외관',
	1: '건물외관측면',
	2: '외부출입계단',
	3: '건물주차장',
	4: '건물 주출입구',
	5: '건물이면출입구',
	6: '건물외부사진1',
	7: '건물외부사진2',
	8: '건물외부사진3',
	9: '건물외부사진4',
	10: '건물내부사진1',
	11: '건물내부사진2',
	12: '건물내부사진3',
	13: '건물내부사진4',
	14: '건물내부사진5',
	15: '건물내부사진6',
	16: '건물내부사진7',
	17: '건물내부사진8',
	18: '건물내부사진9',
	19: '건물내부사진10',
};

/**
 * Money formatter.
 *
 * F-2.2 (2026-04-15) — MUCH / LOAN branches now emit the spec-compliant
 * "{N}억 {M}만원" form (e.g. 530202만원 → "53억 202만원"). Both branches
 * treat post-scaling `l` as 원 and derive 만원-level integers, producing
 *   eok===0 && rem===0  → MUCH: break (leaves `o` empty for spec test
 *                          consistency with legacy behavior; callers
 *                          render '' for 0). LOAN: '0' (legacy).
 *   eok===0             → `{rem.toLocaleString()}만원`
 *   rem===0             → `{eok.toLocaleString()}억`
 *   else                → `{eok.toLocaleString()}억 {rem.toLocaleString()}만원`
 * Negative sign is added via the existing `i` flag.
 *
 * The 4th arg `r` (decimal precision) was previously honoured only in the
 * MUCH branch's float form; with spec-compliant integer form it no longer
 * has a meaningful effect on MUCH/LOAN output. Retained for API stability
 * (no current caller passes it). LITTLE / TAX / MOBILE / MOBILE2 branches
 * are untouched.
 *
 * Inputs: the public wrapper `$formatMoney(e, t)` calls `c(e, t, true)` so
 * `e` is in 만원 (→ scaled to 원 here). `$formatMoneyBasedOnWon` passes
 * `a=false`, i.e. already 원. Either way, `l` here is 원.
 */
function c(e, t, a, r) {
	if ('number' != typeof e) return '0';
	// F-2.2: non-finite (Infinity from div-by-zero, NaN) produces garbage like
	// "∞억 NaN만원" — return empty so callers display blank cells instead.
	if (!isFinite(e)) return '';

	var l = e,
		o = '',
		i = !1;
	l < 0 && ((l *= -1), (i = !0)), a && (l *= 1e4);
	var s = Math.floor(l / 1e8),
		c = Math.floor(l % 1e8),
		m = Math.floor(c / 1e4),
		p = Math.floor(c % 1e4);
	// F-2.2 helper: 원-scale `l` → { eok, remManWon }.
	// Total 만원 = floor(l / 1e4) (원→만원, truncate sub-만원 remainder).
	var totalMan = Math.floor(l / 1e4);
	var eok = Math.floor(totalMan / 1e4);
	var remMan = totalMan - eok * 1e4;
	switch (t) {
		case MONEY_FORMAT_TYPE.MUCH:
			// F-2.2: 만원 단위 정수를 "N억 M만원" 포맷으로 표출.
			// 레거시 동작 보존: l===0 → o='' (break), 음수는 아래 `i` 플래그 처리.
			if (0 === l) break;
			if (eok === 0) {
				// 10000 만원 미만. r 이 0 또는 정수이면 과거 처럼 정수 처리.
				o = remMan.toLocaleString() + '만원';
			} else if (remMan === 0) {
				o = eok.toLocaleString() + '억';
			} else {
				o = eok.toLocaleString() + '억 ' + remMan.toLocaleString() + '만원';
			}
			break;
		case MONEY_FORMAT_TYPE.LITTLE:
			if (0 === l) {
				o = '0';
				break;
			}
			s > 0 && (o = s.toLocaleString() + '억'), m > 0 && (o += m.toLocaleString());
			break;
		case MONEY_FORMAT_TYPE.LOAN:
			// F-2.2: spec-compliant "N억 M만원" 포맷 (LOAN 도 같은 포맷).
			// 원 단위 이하 (p) 는 spec 요구사항이 만원 단위 기준이므로 drop.
			if (0 === l) {
				o = '0';
				break;
			}
			if (eok === 0) {
				o = remMan.toLocaleString() + '만원';
			} else if (remMan === 0) {
				o = eok.toLocaleString() + '억';
			} else {
				o = eok.toLocaleString() + '억 ' + remMan.toLocaleString() + '만원';
			}
			break;
		case MONEY_FORMAT_TYPE.TAX:
			if (0 === l) {
				o = '-원';
				break;
			}
			s > 0 && (o = s.toLocaleString() + '억'),
				m > 0 && (o += ' ' + m.toLocaleString() + '만'),
				p > 0 && (o += ' ' + p.toLocaleString()),
				(o += '원'),
				o.trim();
			break;
		case MONEY_FORMAT_TYPE.MOBILE:
			if (0 === l) {
				o = '0';
				break;
			}
			s > 0 && (o = s.toLocaleString() + '억'), m > 0 && (o += o ? ' ' + m.toLocaleString() : m.toLocaleString());
			break;
		case MONEY_FORMAT_TYPE.MOBILE2:
			if (0 === l) {
				o = '0';
				break;
			}
			s > 0 && (o = s.toLocaleString() + '억'),
				m > 0 && (o += o ? ' ' + m.toLocaleString() + '만' : m.toLocaleString() + '만');
			break;
		case MONEY_FORMAT_TYPE.COMPACT:
			// 마커/팝업 같은 좁은 영역용 컴팩트 포맷. "1.6억" / "4.41억" / "3500만원".
			// F-2.2 이전의 MUCH 동작을 그대로 가져옴.
			if (0 === l) break;
			var cd = void 0;
			if (l >= 1e8) { cd = l / 1e8; o = '억'; }
			else if (l >= 1e4) { cd = l / 1e4; o = '만원'; }
			else if (l >= 1) { cd = l; o = '원'; }
			if (typeof r === 'number') {
				var cu = cd.toString();
				var cE = cu.indexOf('.');
				if (0 === r) cd = parseInt(cd);
				else if (cE > 0) cd = cu.slice(0, cE + 1 + r);
			}
			o = cd + o;
			break;
	}
	return i && (o = '-' + o), o;
}
// axios.defaults.headers.common['X-NCP-APIGW-API-KEY-ID'] = 'egpy3fy0c0';
// axios.defaults.headers.common['X-NCP-APIGW-API-KEY'] = 'hUHbBHnw5smY4JLYbecqBNkEMsjaf55rkGSp1iWv';

function base64toFile(base_data, filename) {
	var arr = base_data.split(','),
		mime = arr[0].match(/:(.*?);/)[1],
		bstr = atob(arr[1]),
		n = bstr.length,
		u8arr = new Uint8Array(n);

	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}

	return new File([u8arr], filename, { type: mime });
}

// class initOptions {
// 	_canvasOptions = {
// 		fileName: '',
// 		allowTaint: true,
// 		useCORS: true,
// 		logging: true,
// 		height: 0,
// 		width: 0,
// 		scale: 1,
// 		dpi: 600,
// 		scrollX: 0,
// 		scrollY: 0,
// 		fileExtension: 'pdf',
// 	};

// 	constructor(canvasOptions) {
// 		this.canvasOptions = _.merge(this._canvasOptions, canvasOptions);
// 	}

// 	get canvasOptions() {
// 		return this._canvasOptions;
// 	}

// 	set canvasOptions(value) {
// 		this._canvasOptions = _.merge(this._canvasOptions, value);
// 	}
// }

// 출력 PDF/프린트 기본 파일명: '위온어스_<빌딩명>'. 빌딩명이 비면 '위온어스'.
// OS 파일명 금지문자(\ / : * ? " < > |) 는 '_' 로, 연속 공백은 1칸으로 정리.
const wReportName = rawName => {
	const name = String(rawName == null ? '' : rawName)
		.replace(/[\\/:*?"<>|]/g, '_')
		.replace(/\s+/g, ' ')
		.trim();
	return name ? `위온어스_${name}` : '위온어스';
};

const methods = {
	reloadPage() {
		window.location.reload();
	},

	py_m2_ex: val => {
		return val == '' ? '' : Number((val * PY_M2_EX).toFixed(2));
	},

	m2_py_ex: val => {
		return val == '' ? '' : Number((val * M2_PY_EX).toFixed(2));
	},
	openNaver: id => {
		var pop = window.open(
			'https://m.land.naver.com/article/info/' + id,
			'pop_naver',
			'left=104,top=0,width=1920, height=1420,scrolling=yes,scrollbars=yes',
		);
		if (pop) {
			pop.focus();
		}
	},

	openNaverMap: jibun_addr => {
		var pop = window.open(
			'http://map.naver.com/index.nhn?query=' + jibun_addr,
			'pop_naver',
			'left=104,top=0,width=1920, height=1420,scrolling=yes,scrollbars=yes',
		);
		if (pop) {
			pop.focus();
		}
	},
	openDaumMap: jibun_addr => {
		var pop = window.open(
			'http://map.daum.net/?q=' + jibun_addr,
			'pop_daum',
			'left=104,top=0,width=1920, height=1420,scrolling=yes,scrollbars=yes',
		);
		if (pop) {
			pop.focus();
		}
	},
	// 블록지도: bloc.ai.kr 공개 지도(/map)에서 해당 필지로 이동 + 디테일 패널 오픈. weonus → bloc 딥링크.
	// (이전 /ouravm 은 ADMIN 전용 AVM 페이지라 일반 사용자는 지도가 안 보였음 — 공개 /map 으로 변경)
	openBlocMap: (lat, lng, pnu) => {
		const base = process.env.VUE_APP_BLOC_BASE || 'https://bloc.ai.kr';
		let url = base + '/map?lat=' + lat + '&lng=' + lng;
		if (pnu) url += '&pnu=' + pnu + '&autoOpenDetail=1';
		var pop = window.open(
			url,
			'pop_bloc',
			'left=104,top=0,width=1920, height=1420,scrolling=yes,scrollbars=yes',
		);
		if (pop) {
			pop.focus();
		}
	},

	// F-5.1: onProgress(current, total) callback for page-level progress indicator.
	//
	// 서버 PDF(/admin/api/pdf) 가 실패했을 때의 폴백 인쇄 경로.
	// 2026-08-11 운영 신고("페이지 사이에 빈 페이지 + 용량 증가", 위온어스_성호빌딩.pdf)의
	// 실제 원인이 여기였다. 실측 재현 결과 섹션 8장짜리 출력이 11장·44.9MB 로 나왔다
	// (같은 물건의 서버 PDF 는 8장·1.2MB). 원인은 이 경로에 **페이지 계약이 없다**는 것:
	//   · A4 가로(1122×794px) 용지에 1170×820px 캔버스를 얹어 페이지마다 넘쳤고,
	//   · 페이지 나눔을 지정하지 않아 어디서 끊길지 브라우저에 맡겼으며,
	//   · 섹션이 17~65px(2~8%)만 넘쳐도 페이지를 통째로 한 장 더 만들어 — 그 페이지는
	//     내용 몇 십 px + 나머지 흰 여백이라 사용자 눈에는 "중간에 낀 빈 페이지" 였다,
	//   · 4배 스케일 캔버스를 무손실로 실어 파일이 40MB 대로 불었다.
	// → 페이지 규격을 서버 PDF 와 같은 1170×820 으로 고정하고 "이미지 1장 = 페이지 1장" 을
	//   명시적으로 강제한다. 소폭 초과는 페이지를 늘리지 않고 축소해서 그 한 장에 담는다.
	exportPrint: async (elementList, onProgress, fileName) => {
		const SCALE = 2;                 // 4 → 2. 인쇄 해상도는 충분하고 파일 크기는 1/4 이 된다.
		const PAGE_WIDTH = 1170;
		const PAGE_HEIGHT = 820;
		const SCALED_PAGE_H = PAGE_HEIGHT * SCALE;
		// 페이지 높이의 이 배수 이내로 넘치는 섹션은 페이지를 늘리지 않고 축소해 한 장에 담는다.
		// 실측(성호빌딩·페레힐)에서 초과량이 2~8% 라 축소는 사실상 눈에 띄지 않는다.
		const SHRINK_LIMIT = 1.25;
		const JPEG_QUALITY = 0.92;       // 무손실 PNG 대신 JPEG 로 실어 용량을 더 줄인다.

		const canvasOptions = {
			allowTaint: false,
			useCORS: true,
			logging: false,
			scale: SCALE,
			dpi: 300,
			scrollX: 0,
			scrollY: 0,
		};

		const total = elementList.length;
		// Fire initial progress so the caller can show 0/N immediately.
		if (typeof onProgress === 'function') onProgress(0, total);

		// 요청받은 섹션이 DOM 에 없으면 **여기서 멈춘다**(Codex 리뷰). 조용히 건너뛰면 페이지가
		// 빠진 채로 "정상 출력된 것처럼" 보이는 자료가 고객에게 나간다 — 출력물에서 가장 위험한
		// 실패 유형이다. 창을 열기 전에 확인해 빈 인쇄창이 뜨는 것도 막는다.
		const missingIds = elementList.filter(id => !document.getElementById(id));
		if (missingIds.length) {
			throw new Error(`출력할 페이지를 찾지 못했습니다: ${missingIds.join(', ')}`);
		}

		const my_window = window.open('', '', `width=${PAGE_WIDTH},height=${PAGE_HEIGHT}`);
		// 브라우저의 "PDF 로 저장" 기본 파일명은 document.title 을 사용한다.
		// 빈 타이틀이면 blob URL 의 UUID 가 노출되므로 '위온어스_<빌딩명>' 으로 지정.
		my_window.document.title = wReportName(fileName);

		const doc = my_window.document;
		const style = doc.createElement('style');
		style.textContent = `
			@page { size: ${PAGE_WIDTH}px ${PAGE_HEIGHT}px; margin: 0; }
			html, body { margin:0; padding:0; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
			.wprint-page {
				width:${PAGE_WIDTH}px; height:${PAGE_HEIGHT}px; overflow:hidden;
				display:flex; align-items:center; justify-content:center;
				page-break-after:always; break-after:page;
				page-break-inside:avoid; break-inside:avoid;
			}
			.wprint-page:last-child { page-break-after:auto; break-after:auto; }
			.wprint-page img { max-width:100%; max-height:100%; display:block; }
		`;

		const pageImages = [];
		for (let i = 0; i < elementList.length; i++) {
			// F-5.1: yield to the event loop between pages so Vue can re-render the
			// progress indicator and the browser does not lock up.
			await new Promise(resolve => requestAnimationFrame(resolve));

			const elementDOM = document.getElementById(elementList[i]);

			// Temporarily remove fixed height so html2canvas captures full content.
			// The CSS class .print-section sets height:820px, so we need
			// !important on the inline style to override it.
			const origStyle = elementDOM.style.cssText;
			elementDOM.style.setProperty('height', 'auto', 'important');
			elementDOM.style.setProperty('min-height', PAGE_HEIGHT + 'px', 'important');
			elementDOM.style.setProperty('overflow', 'visible', 'important');

			let canvas;
			try {
				canvas = await html2canvas(elementDOM, canvasOptions);
			} finally {
				// Restore original inline styles even if html2canvas fails
				elementDOM.style.cssText = origStyle;
			}

			if (canvas.height <= SCALED_PAGE_H * SHRINK_LIMIT) {
				// 한 장으로 나간다. 소폭 초과분은 .wprint-page img 의 max-height 가 축소해 담는다.
				pageImages.push(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
			} else {
				// 페이지 높이의 1.25 배를 넘게 긴 섹션만 페이지 단위로 자른다.
				const cW = canvas.width;
				let yOffset = 0;
				while (yOffset < canvas.height) {
					const sliceH = Math.min(SCALED_PAGE_H, canvas.height - yOffset);
					const pageCanvas = document.createElement('canvas');
					pageCanvas.width = cW;
					pageCanvas.height = SCALED_PAGE_H;
					const ctx = pageCanvas.getContext('2d');

					// Fill background white (to avoid black areas on short last page)
					ctx.fillStyle = '#ffffff';
					ctx.fillRect(0, 0, cW, SCALED_PAGE_H);
					ctx.drawImage(canvas, 0, yOffset, cW, sliceH, 0, 0, cW, sliceH);

					pageImages.push(pageCanvas.toDataURL('image/jpeg', JPEG_QUALITY));
					yOffset += SCALED_PAGE_H;
				}
			}

			// F-5.1: report per-page progress after each canvas is rendered.
			if (typeof onProgress === 'function') onProgress(i + 1, total);
		}

		doc.head.appendChild(style);
		const imgs = pageImages.map(src => {
			const wrap = doc.createElement('div');
			wrap.className = 'wprint-page';
			const img = doc.createElement('img');
			img.src = src;
			wrap.appendChild(img);
			doc.body.appendChild(wrap);
			return img;
		});

		// <img> 는 캔버스와 달리 디코딩이 비동기다. 기다리지 않고 print() 하면 흰 종이가 인쇄된다.
		// decode() 하나에만 기대면 안 된다(Codex 리뷰): 미지원 브라우저거나 reject 하면 그대로
		// 통과해 애초에 막으려던 흰 종이가 다시 나온다. load/error 이벤트를 같이 걸고,
		// 그래도 응답이 없으면 마지막 방어로 시간 제한을 둔다(무한 대기로 출력이 멎는 것 방지).
		await Promise.all(imgs.map(img => new Promise(resolve => {
			let settled = false;
			const finish = () => { if (!settled) { settled = true; resolve(); } };
			if (img.complete && img.naturalWidth > 0) return finish();
			img.addEventListener('load', finish, { once: true });
			img.addEventListener('error', finish, { once: true });
			if (img.decode) img.decode().then(finish, () => {});
			setTimeout(finish, 5000);
		})));

		my_window.focus();
		my_window.print();
		my_window.close();
	},

	exportPdf: async (capIdList, fileName) => {
		try {
			return await methods.exportPdfInner(capIdList, fileName);
		} catch (e) {
			// 여기서 실패하면 호출부(copyChartImg)가 브라우저 인쇄 폴백으로 넘어간다.
			// 그 전환은 지금까지 서버에 아무 흔적도 남기지 않아, 사용자가 규격이 다른
			// 출력물을 받아도 원인을 사후에 추적할 수 없었다(2026-08-11 성호빌딩 신고).
			// 진단용 한 줄을 남기되, 실패해도 폴백 자체는 그대로 진행한다.
			//
			// **절대 await 하지 않는다.** 여기까지 왔다는 건 방금 그 서버에 대한 요청이 실패했다는
			// 뜻이고(서버 다운·네트워크 끊김 포함), 같은 서버로 보내는 이 로그 요청도 응답이
			// 오지 않을 가능성이 높다. keepalive 에는 자체 타임아웃이 없어서, 기다리면 진단 한 줄
			// 때문에 사용자의 인쇄 폴백이 통째로 멈춘다. 보내고 잊고, 3초 뒤 스스로 끊는다.
			try {
				const t = store.getters.getToken;
				const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
				const timer = ctl ? setTimeout(() => ctl.abort(), 3000) : null;
				fetch(process.env.VUE_APP_HOST_BACK + '/admin/api/pdf/fallback-log', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Authorization: t ? t.accessToken : '' },
					body: JSON.stringify({
						reason: String((e && e.message) || e).slice(0, 400),
						url: window.location.href.slice(0, 300),
					}),
					keepalive: true,
					signal: ctl ? ctl.signal : undefined,
				})
					.catch(() => { /* 진단 실패가 출력을 막지 않는다 */ })
					// 성공해도 3초짜리 타이머와 controller 가 남지 않게 즉시 정리한다.
					.then(() => { if (timer) clearTimeout(timer); });
			} catch (ignore) { /* 진단 실패가 출력을 막지 않는다 */ }
			throw e;
		}
	},

	exportPdfInner: async (capIdList, fileName) => {
		const formData = new FormData();
		formData.append('url', window.location.href);
		formData.append('idList', JSON.stringify(capIdList));

		// 서버 렌더러의 출력 화면이 필요로 하는 것은 로그인 상태뿐이다. sessionStorage 의 vuexStore 를
		// 통째로 실으면 안 된다: 검색 화면은 상세창을 열 때마다 결과 ID 목록을 pageList 에 쌓고
		// (store 의 setPage) 그 상태 전체가 vuexStore 로 저장되므로, 하루 종일 쓴 탭에서는 이 문자열이
		// 1MB 를 넘는다(결과 1만 건 검색이면 상세창 17번). 서버 multer 의 텍스트 필드 상한이 1MB 라
		// 그 순간 서버 PDF 가 413(종전엔 무기록 500)으로 죽고 브라우저 인쇄 폴백, 즉 "이전 출력물 +
		// 큰 용량" 으로 넘어갔다(2026-09-18 확인, 8-11 이후 매일 5~23건). 상세·출력 탭은 window.open
		// 으로 열려 검색 탭의 sessionStorage 를 그대로 복사해 받으므로 출력 탭 혼자 작아질 수 없다.
		// 그래서 여기서 필요한 값만 골라 새로 만든다. 옛 키(opList/imgObjList/pdf)는 어디서도 쓰지 않는다.
		const s = store.state || {};
		const slimState = {
			token: s.token, userInfo: s.userInfo, guest: s.guest, institution: s.institution, postST: s.postST,
			pageList: [], pageCnt: 1, clientTypeList: [],
		};
		const storage = { vuexStore: JSON.stringify(slimState) };
		formData.append('stroage', JSON.stringify(storage));

		// Capture map images for server-side replacement.
		// Naver Maps v3 renders tiles as <img> elements (not <canvas>), so use
		// html2canvas to rasterise the entire map container into a single image.
		// 지도 캡처는 서로 독립적이므로 순차 await 대신 Promise.all 로 병렬화한다.
		// 한 지도의 캡처가 실패해도 다른 지도는 정상 업로드되도록 개별 try/catch 유지.
		// 체크 해제된 지도는 display:none 으로 DOM 에 남을 수 있다. 숨겨진 지도를
		// html2canvas 로 캡처하면 비정상(거대) 이미지가 나와 서버 PDF 에 빈 페이지를
		// 유발하므로, 보이는(offsetParent!==null) 지도만 캡처·전송한다.
		const mapElements = [...document.querySelectorAll('[id^="map_"], [id^="mainMap_"]')]
			.filter(el => el.offsetParent !== null);
		const mapResults = await Promise.all(
			Array.from(mapElements).map(async mapEl => {
				try {
					const mapCanvas = await html2canvas(mapEl, { useCORS: true, allowTaint: true });
					const blob = await new Promise(r => mapCanvas.toBlob(r, 'image/jpeg', 0.85));
					return blob ? { id: mapEl.id, blob } : null;
				} catch (e) {
					console.warn('Map capture failed for', mapEl.id, e);
					return null;
				}
			})
		);
		let fileCount = 0;
		let fileBytes = 0;
		mapResults.forEach(r => {
			if (r) {
				formData.append('files', r.blob, r.id + '.jpg');
				fileCount += 1;
				fileBytes += r.blob.size || 0;
			}
		});

		const storeToken = store.getters.getToken;
		const token = storeToken ? storeToken.accessToken : '';

		const res = await fetch(process.env.VUE_APP_HOST_BACK + '/admin/api/pdf', {
			method: 'POST',
			headers: { Authorization: token },
			body: formData,
		});

		if (!res.ok) {
			// 상태 코드만 남기면 원인을 알 수 없다. 서버 PDF 가 실패해 폴백으로 넘어간 건이 매일
			// 있었는데 "HTTP 500" 외에 아무 정보가 없어 원인을 특정하지 못했다(2026-09-18 신고).
			// 응답 본문(오류 메시지)과 보낸 요청의 크기를 함께 실어 fallback-log 로 보낸다.
			const body = await res.text().catch(() => '');
			const excerpt = body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
			throw new Error('PDF generation failed: HTTP ' + res.status
				+ ' [maps=' + mapElements.length + ' files=' + fileCount + ' bytes=' + fileBytes + '] ' + excerpt);
		}

		// 인증 미들웨어(auth.verifyToken)는 **토큰 만료 시 HTTP 200 에 {status:402} JSON** 을 실어
		// 보낸다. res.ok 만 보면 그 JSON 을 그대로 blob 으로 받아 .pdf 로 저장해 버려서, 사용자는
		// "열리지 않는 PDF" 를 손에 쥐고 폴백도 로그도 남지 않는다. 실제 PDF 인지 확인한다.
		const ctype = res.headers.get('content-type') || '';
		if (!/pdf|octet-stream/i.test(ctype)) {
			const body = await res.text().catch(() => '');
			throw new Error('PDF 응답이 아님(content-type=' + ctype + '): ' + body.slice(0, 120));
		}

		const blob = await res.blob();
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = wReportName(fileName) + '.pdf';
		document.body.appendChild(a);
		a.click();
		setTimeout(() => {
			URL.revokeObjectURL(url);
			a.remove();
		}, 1000);
	},

	dateToFormat: (date, format) => {
		return dayjs(date).format(format);
	},

	dateWeekFormat: () => {
		return { s: dayjs().weekday(1).format('YYYY-MM-DD'), e: dayjs().weekday(7).format('YYYY-MM-DD') };
	},

	dateNowFormat: () => {
		return { s: dayjs().format('YYYY-MM-DD'), e: dayjs().format('YYYY-MM-DD') };
	},

	dateMonthFormat: () => {
		return { s: dayjs().startOf('month').format('YYYY-MM-DD'), e: dayjs().endOf('month').format('YYYY-MM-DD') };
	},

	datetimeNowFormat: () => {
		return dayjs().format('YYYY-MM-DD hh:mm:ss');
	},

	datetimeFormat: day => {
		if (typeof day != 'string') {
			return null;
		} else if (day == '0000-00-00' || day == null || !day.length) {
			return null;
		} else {
			return dayjs(day).format('MM-DD hh:mm');
		}
	},

	dateKorFormat: day => {
		if (typeof day != 'string') {
			return '';
		} else if (day == '0000-00-00' || day == null || !day.length) {
			return '';
		} else {
			return dayjs(day).format('YYYY년MM월DD일');
		}
	},

	dateFormat: day => {
		if (typeof day != 'string') {
			return null;
		} else if (day == '0000-00-00' || day == null || !day.length) {
			return null;
		} else {
			return dayjs(day).format('YYYY-MM-DD');
		}
	},

	dateFormat2: day => {
		if (typeof day != 'string') {
			return '';
		} else if (day == '0000-00-00' || day == null || !day.length) {
			return '';
		} else {
			return dayjs(day).format('YYYY.MM.DD');
		}
	},

	dateSub: day => {
		const masTime = new Date(day);
		const todayTime = new Date();
		todayTime.setHours(0, 0, 0, 0);
		const diff = masTime - todayTime;
		const diffDay = Math.floor(diff / (1000 * 60 * 60 * 24));

		if (diffDay < 0) {
			return '';
		} else if (diffDay == 0) {
			return '오늘까지';
		} else {
			return 'D-' + diffDay;
		}
	},

	getFloorString(f_B, f_F) {
		// 지하 1층을 -1 로 적은 옛 매물이 있다(저장은 2026-10-17 부터 부호를 뗀다). 음수도 그 층수로 읽는다.
		f_B = Math.abs(Number(f_B)) || 0;
		f_F = Math.abs(Number(f_F)) || 0;
		let floor_string = '';
		if (f_B > 0) {
			floor_string = '지하' + f_B + '층';
		}
		if (f_F > 0) {
			if (floor_string != '') {
				floor_string += '/';
			}
			floor_string += '지상' + f_F + '층';
		}
		return floor_string;
	},

	getFloorString3(f_B, f_F) {
		// 음수도 그 층수로 읽는다(getFloorString 과 같다).
		f_B = Math.abs(Number(f_B)) || 0;
		f_F = Math.abs(Number(f_F)) || 0;
		let floor_string = '';
		if (f_B > 0) {
			floor_string = 'B' + f_B;
		}
		if (f_F > 0) {
			if (floor_string != '') {
				floor_string += '-';
			}
			floor_string += f_F;
		}
		return floor_string;
	},

	numberToKorean(number) {
		if (number) {
			return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		} else {
			return 0;
		}
	},
	formatMoney(e, t, a) {
		return c(e, t, !0, a);
	},
	formatMoneyBasedOnWon(e, t, a) {
		var r = e;
		return 'string' == typeof e && (r = parseInt(e, 10)), c(r, t, !1, a);
	},

	groupBy(data, key) {
		return data.reduce(function (carry, el) {
			var group = el[key];

			if (carry[group] === undefined) {
				carry[group] = [];
			}

			carry[group].push(el);
			return carry;
		}, {});
	},
	isEmpty(value) {
		if (
			value == '' ||
			value == null ||
			value == undefined ||
			(value != null && typeof value == 'object' && !Object.keys(value).length)
		) {
			return false;
		} else {
			return true;
		}
	},
	pageDataSetting(total, limit, block, page) {
		this.isAllCheck = false;
		const totalPage = Math.ceil(total / limit);
		let currentPage = page;
		const first = currentPage > 1 ? parseInt(currentPage, 10) - parseInt(1, 10) : null;
		const end = totalPage !== currentPage ? parseInt(currentPage, 10) + parseInt(1, 10) : null;

		let startIndex = (Math.ceil(currentPage / block) - 1) * block + 1;
		let endIndex = startIndex + block > totalPage ? totalPage : startIndex + block - 1;
		let list = [];
		for (let index = startIndex; index <= endIndex; index++) {
			list.push(index);
		}
		return { first, end, list, currentPage, totalPage };
	},
	objCompare(pt1, pt2) {
		if (pt1.x === pt2.x && pt1.y === pt2.y) {
			return true;
		}
		return false;
	},

	apiGET: async (_path, opts = {}) => {
		const path = process.env.VUE_APP_HOST_BACK + _path;
		const timeout = opts.timeout || 120000; // 무한 스피너 방지 안전망(2분); 호출부가 더 짧게 override 가능

		let msg = '잠시후 다시 시도 해주세요.';
		try {
			const storeToken = store.getters.getToken;

			if (storeToken) {
				axios.defaults.headers.common.authorization = storeToken.accessToken;
				axios.defaults.headers.common.refresh = storeToken.refreshToken;
			}

			const re = await axios.get(path, { timeout });

			if (re.data.status == 402) {
				const ref = await axios.get(process.env.VUE_APP_HOST_BACK + '/users/refresh', { timeout });
				axios.defaults.headers.common.authorization = ref.data.data.accessToken;
				axios.defaults.headers.common.refresh = ref.data.data.refreshToken;

				store.dispatch('callSetToken', {
					accessToken: ref.data.data.accessToken,
					refreshToken: ref.data.data.refreshToken,
				});

				const re_ = await axios.get(path, { timeout });
				return _normalizeApiAddrs(re_.data);
			} else {
				return _normalizeApiAddrs(re.data);
			}
		} catch (e) {
			const status = e.response?.status;
			// 실패 원인(403 범위 밖 / 그 밖의 오류)에 따라 화면을 달리 그려야 하는 호출부가 쓴다(고객카드, CY). 안내 alert 는 그대로 뜬다.
			if (typeof opts.onError === 'function') opts.onError(status || 0);
			if (status == 400) {
				// Bug AO: 기존엔 `errors` 배열이 있을 때만 alert 했지만, 백엔드에서
				// `{success:false, message:'...'}` 형태로 반환되는 400 (예: /working,
				// /building/work/start 등) 은 사용자에게 표시되지 않고 silent return
				// false 였다. 호출부가 자체 메시지 폴백이 없으면 사용자는 원인 모름.
				// errors[] 가 있으면 그걸 우선, 없으면 message 표시.
				if (e.response?.data?.errors?.length) {
					alert(e.response?.data.errors[0].msg);
					markApiAlert();
				} else if (e.response?.data?.message) {
					alert(e.response.data.message);
					markApiAlert();
				}
				return false;
			}
			if (status == 401) {
				axios.defaults.headers.common.authorization = '';
				axios.defaults.headers.common.refresh = '';

				// 로그인 화면으로 보내므로 호출부가 "저장 실패" 를 덧띄울 필요가 없다.
				markApiAlert();
				gotoLoginByAuth();
				return;
			}
			// 과거엔 `e.response.data.message` 를 optional chain 없이 접근해
			// 네트워크 에러(ETIMEDOUT 등 e.response === undefined) 시 TypeError 가
			// 다시 던져져 호출부 `.then` 이 영영 실행되지 않았다. 스피너 영구 고착.
			// 타임아웃(클라이언트 취소)은 외부기관(건축물대장·스마트국토정보) 지연일 때가 많아 별도 안내.
			const isTimeout = e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '');
			const serverMsg = e.response?.data?.message;
			alert(serverMsg || (isTimeout
				? '서버 응답이 지연되어 요청을 취소했습니다. 외부 기관(건축물대장·스마트국토정보) 일시 지연일 수 있으니 잠시 후 다시 시도해주세요.'
				: `${msg} (${e.code || e.message || 'network'})`));
			markApiAlert();
			return false;
		}
	},

	apiPOST: async (_path, param, opts = {}) => {
		const path = process.env.VUE_APP_HOST_BACK + _path;
		const timeout = opts.timeout || 120000; // 무한 스피너 방지 안전망(2분); 호출부가 더 짧게 override 가능

		let msg = '잠시후 다시 시도 해주세요.';
		try {
			const storeToken = store.getters.getToken;

			if (storeToken) {
				axios.defaults.headers.common.authorization = storeToken.accessToken;
				axios.defaults.headers.common.refresh = storeToken.refreshToken;
			}

			const re = await axios.post(path, param, { timeout });
			if (re.data.status == 402) {
				const ref = await axios.get(process.env.VUE_APP_HOST_BACK + '/users/refresh', { timeout });
				axios.defaults.headers.common.authorization = ref.data.data.accessToken;
				axios.defaults.headers.common.refresh = ref.data.data.refreshToken;

				store.dispatch('callSetToken', {
					accessToken: ref.data.data.accessToken,
					refreshToken: ref.data.data.refreshToken,
				});

				const re_ = await axios.post(path, param, { timeout });
				return _normalizeApiAddrs(re_.data);
			} else {
				return _normalizeApiAddrs(re.data);
			}
		} catch (e) {
			const status = e.response?.status;
			if (status == 400) {
				// Bug AO: apiGET 와 동일 — `{success:false, message:'...'}` 형태 400
				// 응답이 silent 였다. errors[] 우선, 없으면 message 표시.
				if (e.response?.data?.errors?.length) {
					alert(e.response?.data.errors[0].msg);
					markApiAlert();
				} else if (e.response?.data?.message) {
					alert(e.response.data.message);
					markApiAlert();
				}
				return false;
			}
			if (status == 401) {
				axios.defaults.headers.common.authorization = '';
				axios.defaults.headers.common.refresh = '';

				// 로그인 화면으로 보내므로 호출부가 "저장 실패" 를 덧띄울 필요가 없다.
				markApiAlert();
				gotoLoginByAuth();
				return;
			}
			// 5xx 또는 네트워크 실패. 과거엔 `e.response.data.message` 를 optional
			// chain 없이 접근해 `e.response === undefined` 케이스(ETIMEDOUT 등) 에서
			// TypeError 가 catch 바깥으로 던져져 caller `.then` 이 실행 안 되고
			// 로딩 스피너가 영구히 돌았다. 모든 실패 경로에서 false 를 반환해
			// caller 가 일관된 분기를 할 수 있도록 한다.
			// 타임아웃(클라이언트 취소)은 외부기관(건축물대장·스마트국토정보) 지연일 때가 많아 별도 안내.
			const isTimeout = e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '');
			const serverMsg = e.response?.data?.message;
			alert(serverMsg || (isTimeout
				? '서버 응답이 지연되어 요청을 취소했습니다. 외부 기관(건축물대장·스마트국토정보) 일시 지연일 수 있으니 잠시 후 다시 시도해주세요.'
				: `${msg} (${e.code || e.message || 'network'})`));
			markApiAlert();
			return false;
		}
	},

	apiLOGIN: async (_path, param) => {
		const path = process.env.VUE_APP_HOST_BACK + _path;

		try {
			const re = await axios.post(path, param);

			axios.defaults.headers.common.authorization = 'Bearer ' + re.data.token.accessToken;
			axios.defaults.headers.common.refresh = 'Bearer ' + re.data.refreshToken;

			// **새 토큰보다 먼저 앞사람의 신원을 지운다.** 신원(userInfo)은 로그인 응답이 아니라
			// 헤더가 나중에 따로 받아 온다. 앞사람이 master 였고 그 값이 남아 있으면, 새 토큰이
			// 들어온 그 짧은 사이에 저장본이 'master + 다른 사람 토큰' 으로 보여 어드민 탭 공유에
			// 실려 나간다(Codex 리뷰). 신원 조회가 느리거나 실패하면 그 사이가 더 길어진다.
			store.dispatch('callSetUserInfo', {
				mem_id: null, mem_name: null, mem_type: null, mem_uid: null,
				client_view: 'N', rent_client_view: 'N',
			});

			store.dispatch('callSetToken', {
				accessToken: 'Bearer ' + re.data.token.accessToken,
				refreshToken: 'Bearer ' + re.data.token.refreshToken,
			});

			store.dispatch('callSetGuest', re.data.guest != null ? !!re.data.guest : (param.guest || !!re.data.institution));
			store.dispatch('callSetInstitution', re.data.institution || null);

			// axios.defaults.headers.common.authorization = re.headers.authorization;
			// axios.defaults.headers.common.refresh = re.headers.refresh;

			// store.dispatch('callSetToken', { accessToken: re.headers.authorization, refreshToken: re.headers.refresh });

			router.push({
				name: 'MainDashboard',
			});

			return re.data;
		} catch (e) {
			const serverMsg = e.response?.data?.message;
			if (serverMsg) alert(serverMsg);
			else alert(`로그인 요청 실패: ${e.code || e.message || 'network'}`);
			return;
		}
	},

	apiLOGOUT: async (_path, param) => {
		const path = process.env.VUE_APP_HOST_BACK + _path;
		try {
			const re = await axios.post(path, param);

			store.dispatch('callSetToken', { accessToken: '', refreshToken: '' });
			// 신원도 같이 비운다. 토큰만 지우면 'master' 라는 이름표가 남아, 다음 사람이
			// 로그인하는 순간 그 이름표에 남의 토큰이 붙는다(위 로그인 주석과 같은 사고).
			store.dispatch('callSetUserInfo', {
				mem_id: null, mem_name: null, mem_type: null, mem_uid: null,
				client_view: 'N', rent_client_view: 'N',
			});
			store.dispatch('clearClientTypeCache');

			router.push({
				name: 'LoginView',
			});

			return re.data;
		} catch (e) {
			const serverMsg = e.response?.data?.message;
			if (serverMsg) alert(serverMsg);
			else alert(`로그인 요청 실패: ${e.code || e.message || 'network'}`);
			return;
		}
	},

	apiIMG: async (_path, param, imgList) => {
		const path = process.env.VUE_APP_HOST_BACK + _path;
		let msg = '잠시후 다시 시도 해주세요.';
		try {
			const header = { 'Content-Type': 'multipart/form-data' };
			let form = new FormData();
			// let img = files;

			form.append('bid', param);

			for (let i = 0; i < imgList.length; i++) {
				form.append('images', imgList[i].file);

				if (!imgList[i].src && imgList[i].fileSrc) {
					//신규
					form.append('info_' + i, 0);
				} else if (imgList[i].src && imgList[i].fileSrc) {
					//수정
					form.append('info_' + i, 1);
				} else {
					//idle
					form.append('info_' + i, -1);
				}

				// I1: 사진이 있는 슬롯에 한해 사용자 라벨을 함께 전송한다.
				//     - 빈 문자열은 백엔드 normalizeImgLabel 에서 NULL 로 환원.
				//     - veiwSrc / src / fileSrc 어느 하나라도 있으면 사진 row 가 있을 슬롯.
				if (imgList[i].veiwSrc || imgList[i].src || imgList[i].fileSrc) {
					form.append('label_' + i, imgList[i].label ?? '');
				}
			}

			const storeToken = store.getters.getToken;

			if (storeToken) {
				axios.defaults.headers.common.authorization = storeToken.accessToken;
				axios.defaults.headers.common.refresh = storeToken.refreshToken;
			}

			const re = await axios.post(path, form, { ...header, timeout: FILE_TRANSFER_TIMEOUT_MS });
			if (re.data.status == 402) {
				const ref = await axios.get(process.env.VUE_APP_HOST_BACK + '/users/refresh', { timeout: FILE_TRANSFER_TIMEOUT_MS });
				axios.defaults.headers.common.authorization = ref.data.data.accessToken;
				axios.defaults.headers.common.refresh = ref.data.data.refreshToken;

				store.dispatch('callSetToken', {
					accessToken: ref.data.data.accessToken,
					refreshToken: ref.data.data.refreshToken,
				});

				const re_ = await axios.post(path, form, { ...header, timeout: FILE_TRANSFER_TIMEOUT_MS });
				return re_.data;
			} else {
				return re.data;
			}
		} catch (e) {
			const status = e.response?.status;
			if (status == 400) {
				// Bug AO: apiGET/apiPOST 와 동일 — `{success:false, message:'...'}`
				// 형태 400 응답이 silent 였다. errors[] 우선, 없으면 message 표시.
				if (e.response?.data?.errors?.length) {
					alert(e.response?.data.errors[0].msg);
					markApiAlert();
				} else if (e.response?.data?.message) {
					alert(e.response.data.message);
					markApiAlert();
				}
				return false;
			}
			if (status == 401) {
				axios.defaults.headers.common.authorization = '';
				axios.defaults.headers.common.refresh = '';

				// 로그인 화면으로 보내므로 호출부가 "저장 실패" 를 덧띄울 필요가 없다.
				markApiAlert();
				gotoLoginByAuth();
				return;
			}
			// 5xx 또는 네트워크 실패. 과거엔 `e.response.data.message` 를 optional
			// chain 없이 접근해 `e.response === undefined` 케이스(ETIMEDOUT 등) 에서
			// TypeError 가 catch 바깥으로 던져져 caller `.then` 이 실행 안 되고
			// 로딩 스피너가 영구히 돌았다. 모든 실패 경로에서 false 를 반환해
			// caller 가 일관된 분기를 할 수 있도록 한다.
			const serverMsg = e.response?.data?.message;
			alert(serverMsg || `${msg} (${e.code || e.message || 'network'})`);
			return false;
		}
	},

	apiFILE: async (_path, param, fileObj, fileName) => {
		const path = process.env.VUE_APP_HOST_BACK + _path;
		let msg = '잠시후 다시 시도 해주세요.';
		try {
			const header = { 'Content-Type': 'multipart/form-data' };
			let form = new FormData();
			// let img = files;

			form.append('bid', param);
			form.append('fileName', fileName);
			form.append('file', fileObj);

			const storeToken = store.getters.getToken;

			if (storeToken) {
				axios.defaults.headers.common.authorization = storeToken.accessToken;
				axios.defaults.headers.common.refresh = storeToken.refreshToken;
			}

			const re = await axios.post(path, form, { ...header, timeout: FILE_TRANSFER_TIMEOUT_MS });
			if (re.data.status == 402) {
				const ref = await axios.get(process.env.VUE_APP_HOST_BACK + '/users/refresh', { timeout: FILE_TRANSFER_TIMEOUT_MS });
				axios.defaults.headers.common.authorization = ref.data.data.accessToken;
				axios.defaults.headers.common.refresh = ref.data.data.refreshToken;

				store.dispatch('callSetToken', {
					accessToken: ref.data.data.accessToken,
					refreshToken: ref.data.data.refreshToken,
				});

				const re_ = await axios.post(path, form, { ...header, timeout: FILE_TRANSFER_TIMEOUT_MS });
				return re_.data;
			} else {
				return re.data;
			}
		} catch (e) {
			const status = e.response?.status;
			if (status == 400) {
				// Bug AO: 다른 api* 헬퍼와 동일 — `{success:false, message:'...'}`
				// 형태 400 응답이 silent 였다. errors[] 우선, 없으면 message 표시.
				if (e.response?.data?.errors?.length) {
					alert(e.response?.data.errors[0].msg);
					markApiAlert();
				} else if (e.response?.data?.message) {
					alert(e.response.data.message);
					markApiAlert();
				}
				return false;
			}
			if (status == 401) {
				axios.defaults.headers.common.authorization = '';
				axios.defaults.headers.common.refresh = '';

				// 로그인 화면으로 보내므로 호출부가 "저장 실패" 를 덧띄울 필요가 없다.
				markApiAlert();
				gotoLoginByAuth();
				return;
			}
			// 5xx 또는 네트워크 실패. 과거엔 `e.response.data.message` 를 optional
			// chain 없이 접근해 `e.response === undefined` 케이스(ETIMEDOUT 등) 에서
			// TypeError 가 catch 바깥으로 던져져 caller `.then` 이 실행 안 되고
			// 로딩 스피너가 영구히 돌았다. 모든 실패 경로에서 false 를 반환해
			// caller 가 일관된 분기를 할 수 있도록 한다.
			const serverMsg = e.response?.data?.message;
			alert(serverMsg || `${msg} (${e.code || e.message || 'network'})`);
			return false;
		}
	},

	// DC: 파일 하나와 이름 붙은 칸 여럿을 함께 올린다(고객 대화 자료). opts.onProgress 로 진행률을 받는다
	// (통화 녹음은 수십 MB 라 올리는 동안 화면이 멈춘 것처럼 보이지 않게).
	apiFORM: async (_path, fields, file, opts = {}) => {
		const path = process.env.VUE_APP_HOST_BACK + _path;
		// 다시 보낼 때(402 갱신) 같은 FormData 를 재사용하지 않는다. 칸은 파일보다 앞에 둔다.
		const makeForm = () => {
			const form = new FormData();
			for (const [k, v] of Object.entries(fields || {})) {
				if (v !== undefined && v !== null) form.append(k, v);
			}
			form.append('file', file);
			return form;
		};
		const conf = {
			headers: { 'Content-Type': 'multipart/form-data' },
			timeout: FILE_TRANSFER_TIMEOUT_MS,
			onUploadProgress: typeof opts.onProgress === 'function' ? opts.onProgress : undefined,
		};
		try {
			const storeToken = store.getters.getToken;
			if (storeToken) {
				axios.defaults.headers.common.authorization = storeToken.accessToken;
				axios.defaults.headers.common.refresh = storeToken.refreshToken;
			}
			const re = await axios.post(path, makeForm(), conf);
			if (re.data.status == 402) {
				const ref = await axios.get(process.env.VUE_APP_HOST_BACK + '/users/refresh', { timeout: FILE_TRANSFER_TIMEOUT_MS });
				axios.defaults.headers.common.authorization = ref.data.data.accessToken;
				axios.defaults.headers.common.refresh = ref.data.data.refreshToken;
				store.dispatch('callSetToken', {
					accessToken: ref.data.data.accessToken,
					refreshToken: ref.data.data.refreshToken,
				});
				const re_ = await axios.post(path, makeForm(), conf);
				return re_.data;
			}
			return re.data;
		} catch (e) {
			const status = e.response?.status;
			if (status == 401) {
				axios.defaults.headers.common.authorization = '';
				axios.defaults.headers.common.refresh = '';
				markApiAlert();
				gotoLoginByAuth();
				return;
			}
			const data = e.response?.data || {};
			const serverMsg = (data.errors && data.errors.length && data.errors[0].msg) || data.message;
			const isTimeout = e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '');
			alert(serverMsg || (isTimeout ? '올리는 시간이 너무 오래 걸려 멈췄습니다. 잠시 후 다시 시도해 주세요.'
				: `잠시후 다시 시도 해주세요. (${e.code || e.message || 'network'})`));
			markApiAlert();
			return false;
		}
	},

	apiPOST_ADD: async (_path, param) => {
		const path = process.env.VUE_APP_HOST_BACK + _path;
		let msg = '잠시후 다시 시도 해주세요.';
		try {
			const header = { 'Content-Type': 'multipart/form-data' };
			let form = new FormData();
			// let img = files;

			form.append('title', param.title);
			form.append('memo', param.memo);
			form.append('memList', param.memList);

			// console.log(param.fileObj[]);

			for (let i = 0; i < param.fileObj.length; i++) {
				form.append('files', param.fileObj[i]);
			}

			const storeToken = store.getters.getToken;

			if (storeToken) {
				axios.defaults.headers.common.authorization = storeToken.accessToken;
				axios.defaults.headers.common.refresh = storeToken.refreshToken;
			}

			const re = await axios.post(path, form, { ...header, timeout: FILE_TRANSFER_TIMEOUT_MS });
			if (re.data.status == 402) {
				const ref = await axios.get(process.env.VUE_APP_HOST_BACK + '/users/refresh', { timeout: FILE_TRANSFER_TIMEOUT_MS });
				axios.defaults.headers.common.authorization = ref.data.data.accessToken;
				axios.defaults.headers.common.refresh = ref.data.data.refreshToken;

				store.dispatch('callSetToken', {
					accessToken: ref.data.data.accessToken,
					refreshToken: ref.data.data.refreshToken,
				});

				const re_ = await axios.post(path, form, { ...header, timeout: FILE_TRANSFER_TIMEOUT_MS });
				return re_.data;
			} else {
				return re.data;
			}
		} catch (e) {
			const status = e.response?.status;
			if (status == 400) {
				// Bug AO: apiPOST_ADD 도 동일 — `{success:false, message:'...'}`
				// 형태 400 응답이 silent 였다. errors[] 우선, 없으면 message 표시.
				if (e.response?.data?.errors?.length) {
					alert(e.response?.data.errors[0].msg);
					markApiAlert();
				} else if (e.response?.data?.message) {
					alert(e.response.data.message);
					markApiAlert();
				}
				return false;
			}
			if (status == 401) {
				axios.defaults.headers.common.authorization = '';
				axios.defaults.headers.common.refresh = '';

				// 로그인 화면으로 보내므로 호출부가 "저장 실패" 를 덧띄울 필요가 없다.
				markApiAlert();
				gotoLoginByAuth();
				return;
			}
			// 5xx 또는 네트워크 실패. 과거엔 `e.response.data.message` 를 optional
			// chain 없이 접근해 `e.response === undefined` 케이스(ETIMEDOUT 등) 에서
			// TypeError 가 catch 바깥으로 던져져 caller `.then` 이 실행 안 되고
			// 로딩 스피너가 영구히 돌았다. 모든 실패 경로에서 false 를 반환해
			// caller 가 일관된 분기를 할 수 있도록 한다.
			const serverMsg = e.response?.data?.message;
			alert(serverMsg || `${msg} (${e.code || e.message || 'network'})`);
			return false;
		}
	},

	apiDOWN: async (_path, fileType) => {
		const path = process.env.VUE_APP_HOST_BACK + _path;

		let msg = '잠시후 다시 시도 해주세요.';
		try {
			const storeToken = store.getters.getToken;

			if (storeToken) {
				axios.defaults.headers.common.authorization = storeToken.accessToken;
				axios.defaults.headers.common.refresh = storeToken.refreshToken;
			}

			const re = await axios.get(path, {
				timeout: FILE_TRANSFER_TIMEOUT_MS,
				responseType: 'arraybuffer',
				headers: {
					'Content-Type': 'application/json',
					Accept: fileType,
				},
			});

			if (re.data.status == 402) {
				const ref = await axios.get(process.env.VUE_APP_HOST_BACK + '/users/refresh', { timeout: FILE_TRANSFER_TIMEOUT_MS });
				axios.defaults.headers.common.authorization = ref.data.data.accessToken;
				axios.defaults.headers.common.refresh = ref.data.data.refreshToken;

				store.dispatch('callSetToken', {
					accessToken: ref.data.data.accessToken,
					refreshToken: ref.data.data.refreshToken,
				});

				const re_ = await axios.get(path, { timeout: FILE_TRANSFER_TIMEOUT_MS });
				return re_.data;
			} else {
				return re.data;
			}
		} catch (e) {
			const status = e.response?.status;
			if (status == 401) {
				axios.defaults.headers.common.authorization = '';
				axios.defaults.headers.common.refresh = '';

				gotoLoginByAuth();
				return;
			}
			const serverMsg = e.response?.data?.message;
			alert(serverMsg || `${msg} (${e.code || e.message || 'network'})`);
			return false;
		}
	},

	imgResize: async file => {
		var imageResize = new ImageResize({
			format: 'png',
			quality: 0.6,
		});

		let _base64 = await imageResize.play(file);
		let _file = base64toFile(_base64, file.name);

		return { file: _file, base64: _base64 };
	},

	checkPer(mid) {
		// console.log(store.getters.getUserInfo.mem_uid, mid);
		if (store.getters.getUserInfo.mem_uid == mid || store.getters.getUserInfo.mem_type == 'master') {
			return true;
		} else {
			return false;
		}
	},

	fileToBase64(file) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = async () => {
				try {
					resolve(reader.result);
				} catch (error) {
					reject(null);
				}
			};

			reader.onerror = async () => {
				reject(null);
			};
		});
	},
};

export default {
	install(Vue) {
		Vue.config.globalProperties.$reloadPage = methods.reloadPage;
		Vue.config.globalProperties.$m2_py_ex = methods.m2_py_ex;
		Vue.config.globalProperties.$py_m2_ex = methods.py_m2_ex;
		Vue.config.globalProperties.$dateNowFormat = methods.dateNowFormat;
		Vue.config.globalProperties.$dateWeekFormat = methods.dateWeekFormat;
		Vue.config.globalProperties.$dateMonthFormat = methods.dateMonthFormat;
		Vue.config.globalProperties.$openNaver = methods.openNaver;
		Vue.config.globalProperties.$openNaverMap = methods.openNaverMap;
		Vue.config.globalProperties.$openDaumMap = methods.openDaumMap;
		Vue.config.globalProperties.$openBlocMap = methods.openBlocMap;
		Vue.config.globalProperties.$CLIENT_PHONE_REASON = CLIENT_PHONE_REASON;
		Vue.config.globalProperties.$CLIENT_CONTACT_REASON = CLIENT_CONTACT_REASON;
		Vue.config.globalProperties.$INTERIOR_TYPE = INTERIOR_TYPE;
		Vue.config.globalProperties.$RENT_FREE = RENT_FREE;
		Vue.config.globalProperties.$IMG_NAME = IMG_NAME;
		Vue.config.globalProperties.$MONEY1 = MONEY1;
		Vue.config.globalProperties.$MONEY2 = MONEY2;
		Vue.config.globalProperties.$M2_PY_EX = M2_PY_EX;
		Vue.config.globalProperties.$PY_M2_EX = PY_M2_EX;
		Vue.config.globalProperties.$ITEM_STATE = ITEM_STATE;
		Vue.config.globalProperties.$ITEM_STATE2 = ITEM_STATE2;
		Vue.config.globalProperties.$CLIENT_LOG_TYPE = CLIENT_LOG_TYPE;
		Vue.config.globalProperties.$CLIENT_TYPE = CLIENT_TYPE;
		Vue.config.globalProperties.$CLIENT_VIEW_SCOPE = CLIENT_VIEW_SCOPE;
		Vue.config.globalProperties.$CLIENT_VIEW_SCOPE_LIST = CLIENT_VIEW_SCOPE_LIST;
		Vue.config.globalProperties.$WORKBOARD_SCOPE = WORKBOARD_SCOPE;
		Vue.config.globalProperties.$WORKBOARD_SCOPE_LIST = WORKBOARD_SCOPE_LIST;
		Vue.config.globalProperties.$WORKBOARD_SCOPE_RANK_DEFAULT = WORKBOARD_SCOPE_RANK_DEFAULT;
		Vue.config.globalProperties.$COM_INFO = COM_INFO;
		Vue.config.globalProperties.$BUILDING_INFO = BUILDING_INFO;
		Vue.config.globalProperties.$MEM_TYPE = MEM_TYPE;
		Vue.config.globalProperties.$HIRE_STATUS = HIRE_STATUS;
		Vue.config.globalProperties.$LOG_CHG_INFO = LOG_CHG_INFO;
		Vue.config.globalProperties.$MONEY_FORMAT_TYPE = MONEY_FORMAT_TYPE;
		Vue.config.globalProperties.$datetimeFormat = methods.datetimeFormat;
		Vue.config.globalProperties.$dateToFormat = methods.dateToFormat;
		Vue.config.globalProperties.$dateFormat = methods.dateFormat;
		Vue.config.globalProperties.$dateFormat2 = methods.dateFormat2;
		Vue.config.globalProperties.$datetimeNowFormat = methods.datetimeNowFormat;
		Vue.config.globalProperties.$dateSub = methods.dateSub;
		Vue.config.globalProperties.$getFloorString = methods.getFloorString;
		Vue.config.globalProperties.$getFloorString3 = methods.getFloorString3;
		Vue.config.globalProperties.$numberToKorean = methods.numberToKorean;
		Vue.config.globalProperties.$formatMoney = methods.formatMoney;
		Vue.config.globalProperties.$formatMoneyBasedOnWon = methods.formatMoneyBasedOnWon;
		Vue.config.globalProperties.$groupBy = methods.groupBy;
		Vue.config.globalProperties.$isEmpty = methods.isEmpty;
		Vue.config.globalProperties.$pageDataSetting = methods.pageDataSetting;
		Vue.config.globalProperties.$objCompare = methods.objCompare;
		Vue.config.globalProperties.$apiGET = methods.apiGET;
		Vue.config.globalProperties.$apiDOWN = methods.apiDOWN;
		Vue.config.globalProperties.$apiPOST = methods.apiPOST;
		Vue.config.globalProperties.$apiLOGIN = methods.apiLOGIN;
		Vue.config.globalProperties.$apiLOGOUT = methods.apiLOGOUT;
		Vue.config.globalProperties.$apiIMG = methods.apiIMG;
		Vue.config.globalProperties.$apiFILE = methods.apiFILE;
		Vue.config.globalProperties.$apiFORM = methods.apiFORM;
		Vue.config.globalProperties.$apiPOST_ADD = methods.apiPOST_ADD;
		Vue.config.globalProperties.$imgResize = methods.imgResize;
		Vue.config.globalProperties.$exportPrint = methods.exportPrint;
		Vue.config.globalProperties.$exportPdf = methods.exportPdf;
		Vue.config.globalProperties.$dateKorFormat = methods.dateKorFormat;
		Vue.config.globalProperties.$checkPer = methods.checkPer;
		Vue.config.globalProperties.$fileToBase64 = methods.fileToBase64;
		Vue.config.globalProperties.$isAuthRedirect = isAuthRedirect;
		// CQ: API 계층이 이미 사유를 alert 했는지 — 화면이 일반 문구('매물 없음')를 겹쳐 띄우지 않게.
		Vue.config.globalProperties.$didApiAlert = didApiAlert;
		Vue.config.globalProperties.$didApiAlert = didApiAlert;
		Vue.config.globalProperties.$setBusy = setBusy;
		Vue.config.globalProperties.$isBusy = isBusy;
		Vue.config.globalProperties.$markLeaveConfirmed = markLeaveConfirmed;
		Vue.config.globalProperties.$isLeaveConfirmed = isLeaveConfirmed;
	},
};
